import type { Rng } from './rng';
import { sepLabel, type SepLabel } from './playSim';
import type { Tunables } from './tunables';
import type { CatchTiming } from './types';

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export interface PowerBand {
  centre: number;
  half: number;
  lo: number;
  hi: number;
}

/** Ideal power band (GDD 6.4): centre = clamp(35 + 1.2 x distance, 40, 90); half-width = 6 + THA / 15. */
export function idealBand(distanceYards: number, tha: number, t: Tunables): PowerBand {
  const th = t.throw;
  const centre = clamp(th.bandCentreBase + th.bandCentrePerYard * distanceYards, th.bandCentreMin, th.bandCentreMax);
  const half = th.bandHalfWidthBase + tha / th.bandHalfWidthThaDivisor;
  return { centre, half, lo: centre - half, hi: centre + half };
}

/** Points the throw power sits outside the band; 0 inside it. Positive is over, negative is under. */
export function pointsOutside(power: number, band: PowerBand): number {
  if (power > band.hi) return power - band.hi;
  if (power < band.lo) return power - band.lo;
  return 0;
}

/** powerAccuracy (GDD 7): 1.0 in the band, else max(0.4, 1 - 0.03 x points outside). */
export function powerAccuracy(power: number, band: PowerBand, t: Tunables): number {
  const out = Math.abs(pointsOutside(power, band));
  return out === 0 ? 1 : Math.max(t.throw.accuracyFloor, 1 - t.throw.accuracyLossPerPoint * out);
}

/** throwQuality = (QB.THA / 100) x powerAccuracy x (1 - pressurePenalty) */
export function throwQuality(tha: number, powerAcc: number, pressurePenalty: number): number {
  return (tha / 100) * powerAcc * (1 - pressurePenalty);
}

export function powerBonus(power: number, band: PowerBand, t: Tunables): number {
  const out = Math.abs(pointsOutside(power, band));
  if (out === 0) return t.catch.powerBonusInBand;
  return out <= t.catch.powerNearPoints ? t.catch.powerBonusNear : t.catch.powerBonusFar;
}

export interface CatchInputs {
  /** Air distance of the throw in yards. */
  airYards: number;
  wrCth: number;
  defCov: number;
  sep: number;
  timing: CatchTiming;
  powerBonus: number;
  catchTypeMod: number;
  quality: number;
}

/**
 * catchChance = 50 + 0.4 x (WR.CTH - DEF.COV) + 2 x sep + timing + power + catchType + 20 x (quality - 0.8), clamp 8 to 95.
 * Plus an AI-concluded depth term (docs/DECISIONS.md): minus perAirYard for every air yard past airYardsFree.
 */
/**
 * Grades a catch tap by when it lands against the ball's arrival: early is seconds before (positive),
 * a late tap is negative. Perfect sits around the arrival, Good just before it or a moment late,
 * anything earlier is a Miss. No tap at all is Good (see DECISIONS).
 */
export function gradeCatchTap(early: number, t: Tunables): CatchTiming {
  const c = t.controls;
  if (early <= c.catchPerfectEarlySeconds && early >= -c.catchPerfectLateSeconds) return 'perfect';
  if (early <= c.catchGoodEarlySeconds && early >= -c.catchLateGraceSeconds) return 'good';
  return 'miss';
}

export function catchChance(i: CatchInputs, t: Tunables): number {
  const c = t.catch;
  const raw =
    c.base +
    c.ratingFactor * (i.wrCth - i.defCov) +
    c.sepFactor * i.sep +
    c.timingBonus[i.timing] +
    i.powerBonus +
    i.catchTypeMod +
    c.qualityFactor * (i.quality - c.qualityPivot) -
    c.perAirYard * Math.max(0, i.airYards - c.airYardsFree);
  return clamp(raw, c.min, c.max);
}

export interface IntInputs {
  wrCth: number;
  defCov: number;
  sep: number;
  quality: number;
  underthrowBonus: number;
  aggressive: boolean;
}

/** intChance = 0.3 x (DEF.COV - WR.CTH) + (sep < 0 ? -1.5 x sep : 0) + (quality < 0.7 ? 15 : 0) + underthrow + aggressive, clamp 0 to 35. */
export function interceptionChance(i: IntInputs, t: Tunables): number {
  const n = t.interception;
  const raw =
    n.ratingFactor * (i.defCov - i.wrCth) +
    (i.sep < 0 ? -n.negSepFactor * i.sep : 0) +
    (i.quality < n.poorQualityBelow ? n.poorQualityBonus : 0) +
    i.underthrowBonus +
    (i.aggressive ? n.aggressiveBonus : 0);
  return clamp(raw, n.min, n.max);
}

export interface PassInput {
  qbTha: number;
  wrCth: number;
  defCov: number;
  sep: number;
  distanceYards: number;
  /** 0 to 100. Undefined means a tap throw: the band centre. */
  power?: number;
  timing: CatchTiming;
  pressurePenalty: number;
  catchTypeMod: number;
  aggressive: boolean;
}

export type PassOutcome = 'complete' | 'incomplete' | 'interception';

export interface PassResult {
  outcome: PassOutcome;
  band: PowerBand;
  power: number;
  powerAccuracy: number;
  quality: number;
  sep: number;
  sepLabel: SepLabel;
  overthrow: boolean;
  underthrow: boolean;
  intChance: number;
  intRoll: number;
  /** Set when an overthrow sailed the pass. */
  sailRoll: number | null;
  catchChance: number;
  catchRoll: number | null;
  /** Caught chance was high but the ball hit the ground. */
  dropped: boolean;
  timing: CatchTiming;
}

/**
 * Resolves a thrown ball (GDD 7). Rolls in a fixed order so a seed always replays:
 * interception first, then the overthrow sail, then the catch.
 */
export function resolvePass(input: PassInput, rng: Rng, t: Tunables): PassResult {
  const band = idealBand(input.distanceYards, input.qbTha, t);
  const power = input.power ?? band.centre;
  const outside = pointsOutside(power, band);
  const overthrow = outside > t.throw.overthrowMargin;
  const underthrow = outside < -t.throw.underthrowMargin;
  const acc = powerAccuracy(power, band, t);
  const quality = throwQuality(input.qbTha, acc, input.pressurePenalty);

  const intChance = interceptionChance(
    {
      wrCth: input.wrCth,
      defCov: input.defCov,
      sep: input.sep,
      quality,
      underthrowBonus: underthrow ? t.throw.underthrowIntBonus : 0,
      aggressive: input.aggressive,
    },
    t,
  );
  const cc = catchChance(
    {
      airYards: input.distanceYards,
      wrCth: input.wrCth,
      defCov: input.defCov,
      sep: input.sep,
      timing: input.timing,
      powerBonus: powerBonus(power, band, t),
      catchTypeMod: input.catchTypeMod,
      quality,
    },
    t,
  );

  const base = {
    band,
    power,
    powerAccuracy: acc,
    quality,
    sep: input.sep,
    sepLabel: sepLabel(input.sep, t),
    overthrow,
    underthrow,
    intChance,
    catchChance: cc,
    timing: input.timing,
  };

  const int = rng.rollDetailed(intChance);
  if (int.hit) {
    return { ...base, outcome: 'interception', intRoll: int.roll, sailRoll: null, catchRoll: null, dropped: false };
  }
  if (overthrow) {
    const sail = rng.rollDetailed(t.throw.overthrowIncompleteChance);
    if (sail.hit) {
      return { ...base, outcome: 'incomplete', intRoll: int.roll, sailRoll: sail.roll, catchRoll: null, dropped: false };
    }
  }
  const c = rng.rollDetailed(cc);
  return {
    ...base,
    outcome: c.hit ? 'complete' : 'incomplete',
    intRoll: int.roll,
    sailRoll: null,
    catchRoll: c.roll,
    dropped: !c.hit && cc > t.catch.dropLineAbove,
  };
}

/** Phase 1 run-after-catch (M3 replaces it with player control): more separation, more yards. */
export function yardsAfterCatch(sep: number, yardsToEndZone: number, rng: Rng, t: Tunables): { yards: number; breakaway: boolean } {
  const y = t.yac;
  if (sep >= y.breakawaySep && rng.roll(y.breakawayChance)) {
    return { yards: Math.max(0, yardsToEndZone), breakaway: true };
  }
  const raw = y.base + y.perSep * sep + rng.range(-y.noise, y.noise);
  return { yards: Math.min(Math.max(0, Math.round(raw)), y.max, Math.max(0, yardsToEndZone)), breakaway: false };
}
